import { Button, Group, SimpleGrid, Stack, Text, TextInput } from "@mantine/core";
import { useState } from "react";
import type { HouseholdFields } from "./household-queries";

export interface HouseholdFormProps {
  readonly initial: HouseholdFields;
  readonly submitLabel: string;
  readonly loading: boolean;
  readonly failed: boolean;
  readonly onSubmit: (fields: HouseholdFields) => void;
  readonly onCancel: () => void;
}

/** The one household form, used to create a household and to edit one, so the two can never drift apart. */
export function HouseholdForm({
  initial,
  submitLabel,
  loading,
  failed,
  onSubmit,
  onCancel,
}: HouseholdFormProps) {
  const [fields, setFields] = useState<HouseholdFields>(initial);
  const set = (key: keyof HouseholdFields) => (event: { currentTarget: { value: string } }) => {
    const value = event.currentTarget.value;
    setFields((current) => ({ ...current, [key]: value }));
  };

  return (
    <Stack gap="sm" mt="md">
      <TextInput
        label="Household name"
        description="How the church addresses them, such as the Okafor family."
        required
        value={fields.name}
        onChange={set("name")}
      />
      <TextInput label="Address" value={fields.addressLine1} onChange={set("addressLine1")} />
      <TextInput
        label="Address line 2"
        value={fields.addressLine2}
        onChange={set("addressLine2")}
      />
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
        <TextInput label="City" value={fields.locality} onChange={set("locality")} />
        <TextInput label="State" value={fields.region} onChange={set("region")} />
        <TextInput label="ZIP code" value={fields.postalCode} onChange={set("postalCode")} />
      </SimpleGrid>
      <Group justify="flex-end" mt="xs">
        <Button variant="subtle" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          disabled={fields.name.trim().length === 0}
          loading={loading}
          onClick={() => {
            onSubmit(fields);
          }}
        >
          {submitLabel}
        </Button>
      </Group>
      {failed ? (
        <Text size="sm" c="red">
          The household was not saved. Check your access and try again.
        </Text>
      ) : null}
    </Stack>
  );
}
